import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Phone,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Home,
  Building,
  Building2,
  Hospital,
  Users,
  Loader2,
  CreditCard,
  Shield,
  FileText,
  Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Service Request Form
 * 
 * Digital version of ATOA's "Client Service Request Details" form
 * Captures initial intake information when a client is referred
 */

const SERVICE_DAYS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const HOURS_OPTIONS = [
  { value: 4, label: '4 hours' },
  { value: 6, label: '6 hours' },
  { value: 8, label: '8 hours' },
  { value: 10, label: '10 hours' },
  { value: 12, label: '12 hours' },
  { value: 24, label: '24 hours' },
  { value: 0, label: 'Other' },
];

const CARE_LOCATIONS = [
  { key: 'home_alone', label: 'Home Alone', icon: Home },
  { key: 'home_with_family', label: 'Home With Family/Friend', icon: Users },
  { key: 'senior_living', label: 'Senior Living Facility', icon: Building },
  { key: 'assisted_living', label: 'Assisted Living Facility', icon: Building2 },
  { key: 'skilled_nursing', label: 'Skilled Nursing Facility', icon: Hospital },
  { key: 'hospital', label: 'Hospital', icon: Hospital },
  { key: 'other', label: 'Other', icon: MapPin },
];

const ASSISTANCE_AREAS = [
  { key: 'dressing', label: 'Dressing' },
  { key: 'bathing', label: 'Bathing' },
  { key: 'toileting', label: 'Toileting' },
  { key: 'meal_preparation', label: 'Meal Preparation' },
  { key: 'laundry', label: 'Laundry' },
  { key: 'housekeeping', label: 'Housekeeping' },
  { key: 'medication_reminders', label: 'Medication Reminders' },
  { key: 'shopping', label: 'Shopping' },
  { key: 'companionship', label: 'Companionship' },
  { key: 'exercise_activities', label: 'Exercise / Activities' },
  { key: 'escort_appointments', label: 'Escort – Appointments' },
  { key: 'escort_shopping', label: 'Escort – Shopping' },
  { key: 'escort_visits', label: 'Escort – Visits' },
  { key: 'ambulation', label: 'Ambulation (assistance to walk)' },
];

const PAYMENT_TYPES = [
  { key: 'private_pay', label: 'Private Pay' },
  { key: 'ltc_insurance', label: 'Long Term Care Insurance' },
  { key: 'va_benefits', label: 'VA Benefits' },
  { key: 'triwest', label: 'TriWest' },
  { key: 'medicaid', label: 'Medicaid' },
  { key: 'other', label: 'Other' },
];

export default function ServiceRequestForm({ onComplete, onCancel }) {
  const [step, setStep] = useState(1); // 1: Client Info, 2: Schedule, 3: Needs, 4: Payment & Review
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    // Client Info
    client_name: '',
    client_phone: '',
    client_address: '',
    
    // Schedule
    start_of_care_date: '',
    service_days: {},
    hours_per_day: null,
    custom_hours: '',
    begin_time: '',
    end_time: '',
    
    // Care Location
    care_location: 'home_alone',
    care_location_other: '',
    
    // Assistance Areas
    areas_of_assistance: {},
    other_special_needs: '',
    
    // Payment
    payment_type: 'private_pay',
    
    // Representative
    representative_name: '',
    representative_relationship: '',
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      service_days: {
        ...prev.service_days,
        [day]: !prev.service_days[day]
      }
    }));
  };

  const toggleAssistance = (area) => {
    setFormData(prev => ({
      ...prev,
      areas_of_assistance: {
        ...prev.areas_of_assistance,
        [area]: !prev.areas_of_assistance[area]
      }
    }));
  };

  const getSelectedDaysCount = () => {
    return Object.values(formData.service_days).filter(Boolean).length;
  };

  const getSelectedAssistanceCount = () => {
    return Object.values(formData.areas_of_assistance).filter(Boolean).length;
  };

  const calculateWeeklyHours = () => {
    const days = getSelectedDaysCount();
    const hours = formData.hours_per_day || parseInt(formData.custom_hours) || 0;
    return days * hours;
  };

  const handleSubmit = async () => {
    if (!formData.client_name) {
      toast.error('Please enter client name');
      setStep(1);
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/service-requests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: formData.client_name,
          client_phone: formData.client_phone,
          client_address: formData.client_address,
          start_of_care_date: formData.start_of_care_date,
          service_days: formData.service_days,
          hours_per_day: formData.hours_per_day || parseInt(formData.custom_hours) || null,
          begin_time: formData.begin_time,
          end_time: formData.end_time,
          care_location: formData.care_location,
          care_location_other: formData.care_location_other,
          areas_of_assistance: formData.areas_of_assistance,
          other_special_needs: formData.other_special_needs,
          payment_type: formData.payment_type,
          representative_name: formData.representative_name,
          representative_relationship: formData.representative_relationship,
        })
      });

      if (res.ok) {
        const result = await res.json();
        toast.success('Service request created successfully!');
        if (onComplete) {
          onComplete(result.service_request);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create service request');
      }
    } catch (err) {
      console.error('Error creating service request:', err);
      toast.error('Failed to create service request');
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.client_name.trim().length > 0;
      case 2:
        return getSelectedDaysCount() > 0 && (formData.hours_per_day || formData.custom_hours);
      case 3:
        return getSelectedAssistanceCount() > 0;
      case 4:
        return formData.payment_type;
      default:
        return true;
    }
  };

  // Step indicator rendered inline to avoid nested component issues
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <button
            onClick={() => s < step && setStep(s)}
            disabled={s > step}
            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
              s === step
                ? 'bg-[#176DFF] text-white'
                : s < step
                ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200'
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {s < step ? <CheckCircle2 className="w-5 h-5" /> : s}
          </button>
          {s < 4 && (
            <div className={`w-12 h-1 rounded ${s < step ? 'bg-emerald-200' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to CareHub
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#0D1B33]">New Client Intake</h1>
              <p className="text-sm text-[#64748B]">Service Request Details</p>
            </div>
          </div>
        </header>

        {renderStepIndicator()}

        {/* Step 1: Client Information */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
              <h2 className="font-semibold text-[#0D1B33]">Client Information</h2>
              <p className="text-sm text-[#64748B]">Enter the client&apos;s basic information</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    value={formData.client_name}
                    onChange={(e) => updateField('client_name', e.target.value)}
                    placeholder="Full name"
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    value={formData.client_phone}
                    onChange={(e) => updateField('client_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                  Address
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-[#64748B]" />
                  <textarea
                    value={formData.client_address}
                    onChange={(e) => updateField('client_address', e.target.value)}
                    placeholder="Street address, city, state, zip"
                    className="w-full pl-10 pr-4 py-2 border border-[#E2E8F0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                    rows={2}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                    Representative Name
                  </label>
                  <Input
                    value={formData.representative_name}
                    onChange={(e) => updateField('representative_name', e.target.value)}
                    placeholder="Family member or POA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                    Relationship to Client
                  </label>
                  <Input
                    value={formData.representative_relationship}
                    onChange={(e) => updateField('representative_relationship', e.target.value)}
                    placeholder="Son, Daughter, Spouse..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Schedule */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <h2 className="font-semibold text-[#0D1B33]">Service Schedule</h2>
                <p className="text-sm text-[#64748B]">When does the client need care?</p>
              </div>
              <div className="p-5 space-y-6">
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                    Start of Care Date
                  </label>
                  <div className="relative max-w-xs">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                    <Input
                      type="date"
                      value={formData.start_of_care_date}
                      onChange={(e) => updateField('start_of_care_date', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Service Days */}
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-3">
                    Service Days Requested <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {SERVICE_DAYS.map((day) => (
                      <button
                        key={day.key}
                        onClick={() => toggleDay(day.key)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          formData.service_days[day.key]
                            ? 'bg-[#176DFF] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  {getSelectedDaysCount() > 0 && (
                    <p className="text-sm text-emerald-600 mt-2">
                      {getSelectedDaysCount()} day{getSelectedDaysCount() > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                {/* Hours Per Day */}
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-3">
                    Service Hours Per Day <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {HOURS_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          updateField('hours_per_day', option.value === 0 ? null : option.value);
                          if (option.value !== 0) updateField('custom_hours', '');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          formData.hours_per_day === option.value || (option.value === 0 && formData.hours_per_day === null && formData.custom_hours)
                            ? 'bg-[#176DFF] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {formData.hours_per_day === null && (
                    <div className="mt-3 max-w-xs">
                      <Input
                        type="number"
                        value={formData.custom_hours}
                        onChange={(e) => updateField('custom_hours', e.target.value)}
                        placeholder="Enter custom hours"
                        min="1"
                        max="24"
                      />
                    </div>
                  )}
                </div>

                {/* Begin/End Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                      Begin Time
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input
                        type="time"
                        value={formData.begin_time}
                        onChange={(e) => updateField('begin_time', e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                      End Time
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => updateField('end_time', e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                {/* Weekly Hours Summary */}
                {calculateWeeklyHours() > 0 && (
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-900">
                        {calculateWeeklyHours()} hours per week
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Care Location */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <h2 className="font-semibold text-[#0D1B33]">Care Location</h2>
                <p className="text-sm text-[#64748B]">Where will the client receive care?</p>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3">
                  {CARE_LOCATIONS.map((location) => {
                    const Icon = location.icon;
                    return (
                      <button
                        key={location.key}
                        onClick={() => updateField('care_location', location.key)}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          formData.care_location === location.key
                            ? 'border-[#176DFF] bg-[#176DFF]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${
                          formData.care_location === location.key ? 'text-[#176DFF]' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          formData.care_location === location.key ? 'text-[#176DFF]' : 'text-gray-700'
                        }`}>
                          {location.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {formData.care_location === 'other' && (
                  <div className="mt-4">
                    <Input
                      value={formData.care_location_other}
                      onChange={(e) => updateField('care_location_other', e.target.value)}
                      placeholder="Please specify location"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Areas of Assistance */}
        {step === 3 && (
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
              <h2 className="font-semibold text-[#0D1B33]">Areas of Assistance</h2>
              <p className="text-sm text-[#64748B]">Select all areas where the client needs help</p>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-3">
                {ASSISTANCE_AREAS.map((area) => (
                  <button
                    key={area.key}
                    onClick={() => toggleAssistance(area.key)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      formData.areas_of_assistance[area.key]
                        ? 'border-emerald-400 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      formData.areas_of_assistance[area.key]
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-gray-300'
                    }`}>
                      {formData.areas_of_assistance[area.key] && (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className={`text-sm font-medium ${
                      formData.areas_of_assistance[area.key] ? 'text-emerald-700' : 'text-gray-700'
                    }`}>
                      {area.label}
                    </span>
                  </button>
                ))}
              </div>

              {getSelectedAssistanceCount() > 0 && (
                <p className="text-sm text-emerald-600 mt-4">
                  {getSelectedAssistanceCount()} area{getSelectedAssistanceCount() > 1 ? 's' : ''} selected
                </p>
              )}

              <div className="mt-6">
                <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                  Other Special Needs
                </label>
                <textarea
                  value={formData.other_special_needs}
                  onChange={(e) => updateField('other_special_needs', e.target.value)}
                  placeholder="Any additional needs or special instructions..."
                  className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Payment & Review */}
        {step === 4 && (
          <div className="space-y-6">
            {/* Payment Type */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <h2 className="font-semibold text-[#0D1B33]">Payment Type</h2>
                <p className="text-sm text-[#64748B]">How will services be paid for?</p>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3">
                  {PAYMENT_TYPES.map((payment) => (
                    <button
                      key={payment.key}
                      onClick={() => updateField('payment_type', payment.key)}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                        formData.payment_type === payment.key
                          ? 'border-[#176DFF] bg-[#176DFF]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <CreditCard className={`w-5 h-5 ${
                        formData.payment_type === payment.key ? 'text-[#176DFF]' : 'text-gray-400'
                      }`} />
                      <span className={`text-sm font-medium ${
                        formData.payment_type === payment.key ? 'text-[#176DFF]' : 'text-gray-700'
                      }`}>
                        {payment.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <h2 className="font-semibold text-[#0D1B33]">Request Summary</h2>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-[#64748B]">Client</span>
                  <span className="font-medium text-[#0D1B33]">{formData.client_name || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-[#64748B]">Start Date</span>
                  <span className="font-medium text-[#0D1B33]">{formData.start_of_care_date || 'TBD'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-[#64748B]">Days</span>
                  <span className="font-medium text-[#0D1B33]">
                    {SERVICE_DAYS.filter(d => formData.service_days[d.key]).map(d => d.label).join(', ') || '—'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-[#64748B]">Hours/Day</span>
                  <span className="font-medium text-[#0D1B33]">
                    {formData.hours_per_day || formData.custom_hours || '—'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-[#64748B]">Weekly Hours</span>
                  <span className="font-medium text-emerald-600">{calculateWeeklyHours()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-[#64748B]">Location</span>
                  <span className="font-medium text-[#0D1B33]">
                    {CARE_LOCATIONS.find(l => l.key === formData.care_location)?.label || '—'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-[#64748B]">Assistance Areas</span>
                  <span className="font-medium text-[#0D1B33]">{getSelectedAssistanceCount()} selected</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-[#64748B]">Payment</span>
                  <span className="font-medium text-[#0D1B33]">
                    {PAYMENT_TYPES.find(p => p.key === formData.payment_type)?.label || '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* HIPAA Notice */}
            <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">HIPAA Compliant</p>
                <p className="text-sm text-blue-700">All information is securely stored and protected.</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : onCancel()}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {step > 1 ? 'Back' : 'Cancel'}
          </Button>

          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-[#176DFF] hover:bg-[#176DFF]/90"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Submit Service Request
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
